import React, { useState, useEffect, useMemo, Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  Navigate,
  useLocation,
} from "react-router-dom";
import { CartProvider } from "./CartContext";
import ErrorBoundary from "./components/ErrorBoundary";
import LoadingSpinner from "./components/LoadingSpinner";
import CookieConsent from "./components/CookieConsent";
import { AuthStorage, PrivacyManager } from "./utils/storageUtils";
import CartSync from "./CartSync";
import "./css/index.css";
import { DataProvider } from "./Datacontext";
import { WishlistProvider } from "./contexts/WishlistContext";

// ---- Main site pages ---------------------------------------------------------
import MainHome from "./MainHome";
import MainAbout from "./MainAbout";
import Projects from "./projects";
//-------------------------------------------------------------------------------

const safeLazy = (importFn, name) =>
  lazy(() =>
    importFn().then((mod) => {
      if (mod && mod.default != null) return mod;
      console.error(
        `safeLazy: "${name}" has no default export.`,
        "Keys:",
        Object.keys(mod),
      );
      const firstValid = Object.values(mod).find(
        (v) => typeof v === "function" || (typeof v === "object" && v !== null),
      );
      if (firstValid) {
        console.warn(
          `⚠️ safeLazy: Using named export as fallback for "${name}"`,
        );
        return { default: firstValid };
      }
      throw new Error(
        `Module "${name}" has no usable export. Keys: ${Object.keys(mod).join(", ")}`,
      );
    }),
  );

const getToken = () => sessionStorage.getItem("authToken") || "";

/**
 * Decodes a JWT and returns the payload, or null on failure.
 */
const parseJwt = (token) => {
  try {
    return JSON.parse(atob(token.split(".")[1]));
  } catch {
    return null;
  }
};

const getValidToken = () => {
  const token = sessionStorage.getItem("authToken");
  if (!token) return null;
  const decoded = parseJwt(token);
  if (decoded && decoded.exp && decoded.exp * 1000 < Date.now() - 30000) {
    // Token has expired — clear session
    sessionStorage.removeItem("authToken");
    sessionStorage.removeItem("isLoggedIn");
    sessionStorage.removeItem("isAdmin");
    sessionStorage.removeItem("userId");
    sessionStorage.removeItem("userEmail");
    console.warn("[App2] Auth token has expired. User has been logged out.");
    return null;
  }
  return token;
};

const ShopHomepage = safeLazy(() => import("./Homepage"), "Homepage");
const AnnouncementBar = safeLazy(
  () => import("./AnnouncementBar"),
  "AnnouncementBar",
);
const AdminAnnouncements = safeLazy(
  () => import("./admin/AdminAnnouncements"),
  "AdminAnnouncements",
);
const About = safeLazy(() => import("./About1"), "About1");
const Cart = safeLazy(() => import("./pages/Cart"), "Cart");
const Login = safeLazy(() => import("./pages/Login"), "Login");
const VerifyEmail = safeLazy(() => import("./verifyemail"), "verifyemail");
const VerifySuccess = safeLazy(
  () => import("./VerifySuccess"),
  "VerifySuccess",
);
const VerifyFailed = safeLazy(() => import("./VerifyFailed"), "VerifyFailed");
const Shop = safeLazy(() => import("./pages/Shop"), "Shop");
const Contact2 = safeLazy(() => import("./contact2"), "contact2");
const Contact1 = safeLazy(() => import("./Contact1"), "Contact1");
const Admin = safeLazy(() => import("./Admin"), "Admin");
const AdminCategories = safeLazy(
  () => import("./admin/AdminCategories"),
  "AdminCategories",
);
const AdminUsers = safeLazy(() => import("./admin/AdminUsers"), "AdminUsers");
const AdminUserDetails = safeLazy(
  () => import("./admin/AdminUserDetails"),
  "AdminUserDetails",
);
const AdminOrders = safeLazy(() => import("./admin/AdminOrders"), "AdminOrders");
const AdminOrderDetails = safeLazy(
  () => import("./admin/AdminOrderDetails"),
  "AdminOrderDetails",
);
const AdminDiscontinued = safeLazy(
  () => import("./admin/AdminDiscontinued"),
  "AdminDiscontinued",
);
const AdminParcels = safeLazy(() => import("./admin/AdminParcels"), "AdminParcels");
const MyOrders = safeLazy(() => import("./MyOrders"), "MyOrders");
const OrderDetails = safeLazy(() => import("./OrderDetails"), "OrderDetails");
const Account = safeLazy(() => import("./Account"), "Account");
const DeletedItems = safeLazy(
  () => import("./DeletedItemsPage"),
  "DeletedItemsPage",
);
const AdminReviews = safeLazy(
  () => import("./components/AdminReviews"),
  "AdminReviews",
);
const FAQ = safeLazy(() => import("./FAQ"), "FAQ");
const TermsAndConditions = safeLazy(() => import("./T&C"), "T&C");
const ReturnAndExchangePolicy = safeLazy(() => import("./Return"), "Return");
const TermsOfService = safeLazy(
  () => import("./TermsofService"),
  "TermsofService",
);
const PrivacyPolicy = safeLazy(
  () => import("./pages/PrivacyPolicy"),
  "PrivacyPolicy",
);
const ProductDetails = safeLazy(
  () => import("./pages/ProductDetails"),
  "ProductDetails",
);
const CheckoutPage = safeLazy(() => import("./pages/Checkout"), "Checkout");
const Payment = safeLazy(() => import("./Payment"), "Payment");
const ThankYou = safeLazy(() => import("./ThankYou"), "ThankYou");
const Cancelled = safeLazy(() => import("./Cancelled"), "Cancelled");
const Wishlist = safeLazy(() => import("./Wishlist"), "Wishlist");
const PCBuilder = safeLazy(() => import("./pages/PCbuilder"), "PCbuilder");
const ForgotPassword = safeLazy(
  () => import("./pages/ForgotPassword"),
  "ForgotPassword",
);

// Routes that belong to the main company site (no AnnouncementBar)
const MAIN_SITE_PATHS = ["/", "/about", "/projects", "/contact1"];

function AnnouncementBarGate() {
  const { pathname } = useLocation();
  if (MAIN_SITE_PATHS.includes(pathname)) return null;
  return (
    <Suspense fallback={null}>
      <AnnouncementBar />
    </Suspense>
  );
}

// ── Route guard helpers ───────────────────────────────────────────────────────

function RequireAuth({ children }) {
  const loggedIn = sessionStorage.getItem("isLoggedIn") === "true";
  if (!loggedIn) return <Navigate to="/login" replace />;
  return children;
}

function RequireAdmin({ children }) {
  const loggedIn = sessionStorage.getItem("isLoggedIn") === "true";
  const isAdmin = sessionStorage.getItem("isAdmin") === "true";

  if (!loggedIn) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/shop" replace />;
  return children;
}

// ── ComingSoon placeholder ────────────────────────────────────────────────────

function ComingSoon({ title, backTo = "/", backLabel = "Back to Home" }) {
  return (
    <div
      style={{
        minHeight: "60vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Segoe UI', system-ui, sans-serif",
        color: "#212529",
        gap: 12,
        padding: "40px 20px",
        textAlign: "center",
      }}
    >
      <div style={{ fontSize: 48 }}>🚧</div>
      <h1
        style={{
          fontSize: "clamp(1.4rem,3vw,2rem)",
          fontWeight: 800,
          margin: 0,
        }}
      >
        {title}
      </h1>
      <p style={{ color: "#6c757d", fontSize: 15, maxWidth: 400, margin: 0 }}>
        This page is coming soon. Check back shortly!
      </p>
      <Link
        to={backTo}
        style={{
          marginTop: 16,
          padding: "11px 24px",
          background: "#023e8a",
          color: "#fff",
          borderRadius: 8,
          fontWeight: 700,
          textDecoration: "none",
          fontSize: 14,
        }}
      >
        {backLabel}
      </Link>
    </div>
  );
}

// ── AppContent ────────────────────────────────────────────────────────────────

function AppContent() {
  const [isAdmin, setIsAdmin] = useState(sessionStorage.getItem("isAdmin") === "true");
  const [isLoggedIn, setIsLoggedIn] = useState(sessionStorage.getItem("isLoggedIn") === "true");
  const [currentUser, setCurrentUser] = useState(null);
  const [deleted, setDeleted] = useState([]);
  const [authLoading, setAuthLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [cookiesAccepted, setCookiesAccepted] = useState(false);
  const [showCookieConsent, setShowCookieConsent] = useState(false);

  const API_BASE = process.env.REACT_APP_API_URL || "http://40.40.69.79:3051";
  const memoizedDeleted = useMemo(() => deleted, [deleted]);

  // ── Restore admin state on focus / mount ──
  useEffect(() => {
    const init = () => {
      try {
        const storedIsAdmin = sessionStorage.getItem("isAdmin");
        const storedIsLoggedIn = sessionStorage.getItem("isLoggedIn");
        if (storedIsLoggedIn === "true" && storedIsAdmin === "true")
          setIsAdmin(true);
      } catch (e) {
        console.error("Error initializing admin state:", e);
      }
    };
    init();
    window.addEventListener("focus", init);
    return () => window.removeEventListener("focus", init);
  }, []);

  // ── Cookie consent ──
  useEffect(() => {
    const status = PrivacyManager.getConsentStatus();
    if (status === "pending") {
      setShowCookieConsent(true);
    } else if (status === "accepted") {
      setCookiesAccepted(true);
      const storedUser = AuthStorage.loadUserLogin();
      if (storedUser) {
        setCurrentUser(storedUser);
        setIsLoggedIn(true);
        setCurrentUserId(storedUser.uid);
      }
    }
  }, []);

  const updateUserStatus = async (userId, isActive) => {
    if (!userId) return;
    try {
      await fetch(
        `http://40.40.69.79:3051/api/v1/seritonex/user/${isActive ? "activate" : "deactivate"}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify({ user_id: userId }),
        },
      );
    } catch {
      // Silently ignore – endpoint may not be available
    }
  };

  /**
   * Called by Login after a successful API login.
   * isAdminUser comes directly from the API response (is_admin === 1).
   */
  const handleLogin = (isAdminUser, userEmail, userId) => {
    setIsLoggedIn(true);
    setIsAdmin(!!isAdminUser);
    setCurrentUserId(userId);
    const userData = { uid: userId, email: userEmail, isAdmin: !!isAdminUser };
    setCurrentUser(userData);

    try {
      sessionStorage.setItem("isLoggedIn", "true");
      sessionStorage.setItem("isAdmin", String(!!isAdminUser));
      if (userEmail) sessionStorage.setItem("userEmail", userEmail);
      if (userId && !(typeof userId === "string" && userId.includes("@"))) {
        sessionStorage.setItem("userId", String(userId));
      } else {
        sessionStorage.removeItem("userId");
      }
    } catch (e) {
      console.error("Error storing auth state:", e);
    }

    if (cookiesAccepted) AuthStorage.saveUserLogin(userData);
    updateUserStatus(userId, true);
  };

  const handleLogout = async () => {
    if (currentUserId) await updateUserStatus(currentUserId, false);
    try {
      // Pure custom API auth — just clear sessionStorage
    } catch {}

    setIsAdmin(false);
    setIsLoggedIn(false);
    setCurrentUser(null);
    setCurrentUserId(null);

    try {
      [
        "isAdmin",
        "isLoggedIn",
        "userEmail",
        "userId",
        "authToken",
        "seritonex_cart",
      ].forEach((k) => sessionStorage.removeItem(k));
    } catch {}

    if (cookiesAccepted) AuthStorage.clearUserLogin();
  };

  const handleCookieAccept = () => {
    setCookiesAccepted(true);
    setShowCookieConsent(false);
    PrivacyManager.setConsent("accepted");
    if (isLoggedIn && currentUser) AuthStorage.saveUserLogin(currentUser);
  };

  const handleCookieDecline = () => {
    setCookiesAccepted(false);
    setShowCookieConsent(false);
    PrivacyManager.setConsent("declined");
    AuthStorage.clearUserLogin();
  };

  // ── Auth listener ──
  useEffect(() => {
    let mounted = true;

    const checkCustomAuth = () => {
      try {
        const loggedIn = sessionStorage.getItem("isLoggedIn");
        const admin = sessionStorage.getItem("isAdmin");
        const email = sessionStorage.getItem("userEmail");
        const uid = sessionStorage.getItem("userId");
        if (loggedIn === "true") {
          setIsLoggedIn(true);
          setIsAdmin(admin === "true");
          setCurrentUserId(uid);
          if (uid) updateUserStatus(uid, true);
          if (email) {
            setCurrentUser({
              email,
              uid: uid || `custom_${Date.now()}`,
              displayName: email.split("@")[0],
              customAuth: true,
            });
          }
        }
      } catch (e) {
        console.error("Error checking stored auth:", e);
      }
    };

    checkCustomAuth();

    // Auth is purely sessionStorage-based — no external listener needed
    setAuthLoading(false);

    return () => {
      mounted = false;
    };
  }, []);

  // ── Track active/inactive on tab visibility ──
  useEffect(() => {
    const onVisibility = () => {
      if (currentUserId && isLoggedIn)
        updateUserStatus(currentUserId, !document.hidden);
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [currentUserId, isLoggedIn]);

  if (authLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#f5f5f5",
        }}
      >
        <LoadingSpinner size="large" />
      </div>
    );
  }

  // ── Routes ───────────────────────────────────────────────────────────────────
  return (
    <ErrorBoundary>
      <CartProvider>
        <WishlistProvider currentUser={currentUser} isLoggedIn={isLoggedIn}>
        <Router>
          <CartSync user={currentUser} />
          <AnnouncementBarGate />

          <Suspense fallback={<LoadingSpinner size="large" overlay />}>
            <Routes>
              {/* ══ MAIN SITE ══════════════════════════════════════════════════ */}
              <Route path="/" element={<MainHome />} />
              <Route path="/about" element={<MainAbout />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/contact1" element={<Contact1 />} />

              {/* ══ SHOP ═══════════════════════════════════════════════════════ */}
              <Route
                path="/shop"
                element={
                  <ErrorBoundary>
                    <ShopHomepage
                      currentUser={currentUser}
                      isLoggedIn={isLoggedIn}
                    />
                  </ErrorBoundary>
                }
              />
              <Route
                path="/shop/products"
                element={
                  <ErrorBoundary>
                    <Shop currentUser={currentUser} isLoggedIn={isLoggedIn} />
                  </ErrorBoundary>
                }
              />
              <Route
                path="/productdetails/:id"
                element={
                  <ErrorBoundary>
                    <ProductDetails
                      currentUser={currentUser}
                      isLoggedIn={isLoggedIn}
                    />
                  </ErrorBoundary>
                }
              />
              <Route
                path="/cart"
                element={
                  <ErrorBoundary>
                    <Cart />
                  </ErrorBoundary>
                }
              />
              <Route
                path="/checkout"
                element={
                  <RequireAuth>
                    <ErrorBoundary>
                      <CheckoutPage user={currentUser} />
                    </ErrorBoundary>
                  </RequireAuth>
                }
              />
              <Route
                path="/payment"
                element={
                  <RequireAuth>
                    <ErrorBoundary>
                      <Payment />
                    </ErrorBoundary>
                  </RequireAuth>
                }
              />
              <Route path="/thank-you" element={<ThankYou />} />
              <Route path="/cancelled" element={<Cancelled />} />
              <Route path="/contact2" element={<Contact2 />} />
              <Route path="/About1" element={<About />} />

              {/* ══ PROTECTED: logged-in users only ════════════════════════════ */}
              <Route
                path="/account"
                element={
                  <RequireAuth>
                    <Account onLogout={handleLogout} />
                  </RequireAuth>
                }
              />
              <Route
                path="/wishlist"
                element={
                  <RequireAuth>
                    <Wishlist
                      currentUser={currentUser}
                      isLoggedIn={isLoggedIn}
                    />
                  </RequireAuth>
                }
              />
              <Route
                path="/orders"
                element={
                  <RequireAuth>
                    <MyOrders />
                  </RequireAuth>
                }
              />
              <Route
                path="/orders/:orderId"
                element={
                  <RequireAuth>
                    <OrderDetails />
                  </RequireAuth>
                }
              />

              {/* ══ AUTH ═══════════════════════════════════════════════════════ */}
              <Route path="/login" element={<Login onLogin={handleLogin} />} />
              <Route
                path="/register"
                element={<Login onLogin={handleLogin} />}
              />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/verify-success" element={<VerifySuccess />} />
              <Route path="/verify-failed" element={<VerifyFailed />} />

              {/* ══ LEGAL ══════════════════════════════════════════════════════ */}
              <Route path="/faq" element={<FAQ />} />
              <Route path="/FAQ" element={<Navigate to="/faq" replace />} />
              <Route
                path="/terms-and-conditions"
                element={<TermsAndConditions />}
              />
              <Route
                path="/T&C"
                element={<Navigate to="/terms-and-conditions" replace />}
              />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route
                path="/TermsofService"
                element={<Navigate to="/terms-of-service" replace />}
              />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route
                path="/privacypolicy"
                element={<Navigate to="/privacy-policy" replace />}
              />
              <Route
                path="/return-and-exchange"
                element={<ReturnAndExchangePolicy />}
              />
              <Route
                path="/Return"
                element={<Navigate to="/return-and-exchange" replace />}
              />

              {/* ══ COMING SOON stubs ══════════════════════════════════════════ */}
              <Route
                path="/technical-support"
                element={
                  <ComingSoon
                    title="Technical Support"
                    backTo="/contact1"
                    backLabel="Back to Contact"
                  />
                }
              />
              <Route
                path="/delivery"
                element={
                  <ComingSoon
                    title="Delivery Information"
                    backTo="/shop/products"
                    backLabel="Back to Shop"
                  />
                }
              />
              <Route
                path="/legal"
                element={
                  <ComingSoon
                    title="Legal Notice"
                    backTo="/privacy-policy"
                    backLabel="Back to Privacy Policy"
                  />
                }
              />
              <Route
                path="/deals"
                element={<Navigate to="/shop/products" replace />}
              />
              <Route
                path="/pc-builder"
                element={<PCBuilder />}
              />

              {/* ══ ADMIN — strictly requires is_admin === 1 from API ══════════
                  Non-admins are bounced to /shop with no indication the panel exists.
              ═══════════════════════════════════════════════════════════════════ */}
              <Route
                path="/admin"
                element={
                  <RequireAdmin>
                    <ErrorBoundary>
                      <Admin
                        deleted={memoizedDeleted}
                        setDeleted={setDeleted}
                        onLogout={handleLogout}
                      />
                    </ErrorBoundary>
                  </RequireAdmin>
                }
              />
              <Route
                path="/admin/products"
                element={
                  <RequireAdmin>
                    <ErrorBoundary>
                      <Admin
                        deleted={memoizedDeleted}
                        setDeleted={setDeleted}
                        onLogout={handleLogout}
                      />
                    </ErrorBoundary>
                  </RequireAdmin>
                }
              />
              <Route
                path="/admin/categories"
                element={
                  <RequireAdmin>
                    <ErrorBoundary>
                      <AdminCategories />
                    </ErrorBoundary>
                  </RequireAdmin>
                }
              />
              <Route
                path="/admin/announcement"
                element={
                  <RequireAdmin>
                    <ErrorBoundary>
                      <AdminAnnouncements />
                    </ErrorBoundary>
                  </RequireAdmin>
                }
              />
              <Route
                path="/admin/announcements"
                element={
                  <RequireAdmin>
                    <ErrorBoundary>
                      <AdminAnnouncements />
                    </ErrorBoundary>
                  </RequireAdmin>
                }
              />
              <Route
                path="/admin/orders"
                element={
                  <RequireAdmin>
                    <ErrorBoundary>
                      <AdminOrders />
                    </ErrorBoundary>
                  </RequireAdmin>
                }
              />
              <Route
                path="/admin/orders/:id"
                element={
                  <RequireAdmin>
                    <ErrorBoundary>
                      <AdminOrderDetails />
                    </ErrorBoundary>
                  </RequireAdmin>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <RequireAdmin>
                    <ErrorBoundary>
                      <AdminUsers />
                    </ErrorBoundary>
                  </RequireAdmin>
                }
              />
              <Route
                path="/admin/users/:id"
                element={
                  <RequireAdmin>
                    <ErrorBoundary>
                      <AdminUserDetails />
                    </ErrorBoundary>
                  </RequireAdmin>
                }
              />
              <Route
                path="/admin/parcels"
                element={
                  <RequireAdmin>
                    <ErrorBoundary>
                      <AdminParcels />
                    </ErrorBoundary>
                  </RequireAdmin>
                }
              />
              <Route
                path="/admin/reviews"
                element={
                  <RequireAdmin>
                    <ErrorBoundary>
                      <AdminReviews />
                    </ErrorBoundary>
                  </RequireAdmin>
                }
              />
              <Route
                path="/admin/deleted"
                element={
                  <RequireAdmin>
                    <ErrorBoundary>
                      <DeletedItems
                        deleted={memoizedDeleted}
                        setDeleted={setDeleted}
                      />
                    </ErrorBoundary>
                  </RequireAdmin>
                }
              />
              <Route
                path="/admin/discontinued"
                element={
                  <RequireAdmin>
                    <ErrorBoundary>
                      <AdminDiscontinued
                        deleted={memoizedDeleted}
                        setDeleted={setDeleted}
                      />
                    </ErrorBoundary>
                  </RequireAdmin>
                }
              />
            </Routes>
          </Suspense>
        </Router>
        </WishlistProvider>
      </CartProvider>

      {showCookieConsent && (
        <CookieConsent
          onAccept={handleCookieAccept}
          onDecline={handleCookieDecline}
        />
      )}
    </ErrorBoundary>
  );
}

// ── Root App ─────────────────────────────────────────────────────────────────
function App() {
  return (
    <ErrorBoundary>
      <DataProvider>
        <AppContent />
      </DataProvider>
    </ErrorBoundary>
  );
}

export default App;
